// © Microsoft Corporation. All rights reserved.

import { MsnProductType, ProductType, initializeSubscriptionSdk, renderCreateSubscription } from "@msnews/subscription-sdk-wrapper";
import { addOrUpdateTelemetryObject, enums, getExternalURL, placeholderImages, renderHybrid } from "@msnews/social-utils";

import React from "react";
import { SocialProps } from "../../Social.props";
import { classNames } from "@microsoft/fast-web-utilities";
import { getXPayToken } from "@msnews/social-data-service";
import { hasPremiumSubscription } from "../../utils/subscriptionHelpers";
import { gradientOverlayTelemetryContracts, GradientOverlayTelemetryObjects } from "./GradientOverlay.telemetry";

interface GradientOverlayState {
    overlayVisible: boolean;
}

export default class GradientOverlay extends React.Component<SocialProps, GradientOverlayState> {
    private subscriptionChecked: boolean;
    private buttonAnonymouslyClicked: boolean;
    telemetryObjects: GradientOverlayTelemetryObjects;

    constructor(props) {
        super(props);
        this.state = {
            overlayVisible: false
        };
        this.telemetryObjects= null;
    }

    componentDidMount() {
        initializeSubscriptionSdk();
        const { me, gradientOverlayData } = this.props;
        if (!me) {
            this.props.usersConnector.fetchMe();
        } else if (gradientOverlayData) {
            this.checkSubscription();
        }
    }

    componentDidUpdate(prevProps) {
        const { me, gradientOverlayData } = this.props;
        if (!this.subscriptionChecked && me && gradientOverlayData) {
            this.checkSubscription();
        }

        // Take action if user has logged-in after clicking the subscribe button
        if (prevProps.me?.state == enums.userStateType.anonymous && this.props.me?.state != enums.userStateType.anonymous) {
            if (this.buttonAnonymouslyClicked === true) {
                this.renderCreateSubscriptionPanel();
                this.buttonAnonymouslyClicked = undefined;
            }
        }
    }

    async checkSubscription() {
        this.subscriptionChecked = true;
        const { me, gradientOverlayData } = this.props;
        if (gradientOverlayData.subscriptionProductType === MsnProductType.Premium) {
            const isAnonymous = me && me.state == enums.userStateType.anonymous;
            if (isAnonymous) {
                this.activateComponent();
            } else {
                if (!gradientOverlayData.publisherProfileId) {
                    return this.activateComponent();
                }

                hasPremiumSubscription(me, gradientOverlayData.publisherProfileId).then(hasSubscription => {
                    if (!hasSubscription) {
                        this.activateComponent();
                    }
                });
            }
        }
    }

    activateComponent() {
        const { me } = this.props;
        const isAnonymous = me && me.state == enums.userStateType.anonymous;

        document.body.style.overflow = "hidden";
        if (isAnonymous) {
            this.setState(() => ({ overlayVisible: true }));
        } else {
            this.renderCreateSubscriptionPanel();
        }
    }

    async onButtonClick(e) {
        const { me } = this.props;
        const isAnonymous = me && me.state == enums.userStateType.anonymous;
        if (isAnonymous) {
            this.buttonAnonymouslyClicked = true;
            return this.props.popupConnector.openPopup("multiLoginPage");
        } else {
            this.renderCreateSubscriptionPanel();
        }
    }

    async renderCreateSubscriptionPanel() {
        const { gradientOverlayData, me, market } = this.props;
        await renderCreateSubscription({
            publisherId: gradientOverlayData.publisherProfileId,
            publisherName: gradientOverlayData.publisherName,
            publisherLogoUrl: gradientOverlayData.publisherLogoUrl,
            productType: ProductType.Premium,
            userProfilePageUrl: getExternalURL(`${enums.externalUrls.profilePage}${me.id}`, market),
            locale: market,
            authToken: await getXPayToken(me.id),
            userProfileImageUrl: me.primaryAvatar || placeholderImages.avatar,
            scenario: "article",
            onSuccess: () => {
                window.location.reload();
            }
        });
        this.setState(() => ({ overlayVisible: false }));
    }

    render() {
        const { t, managedClasses, gradientOverlayData, mainTelemetryObject } = this.props;
        if (!gradientOverlayData || !this.state.overlayVisible) { return null; }

        if (mainTelemetryObject) {
            this.telemetryObjects = {
                readFullContentButton: addOrUpdateTelemetryObject(mainTelemetryObject, gradientOverlayTelemetryContracts.readFullContentButton, { id: gradientOverlayData.publisherProfileId }, this.telemetryObjects && this.telemetryObjects.readFullContentButton)
            };
        }

        return (
            <div className={classNames(managedClasses.gradientOverlayContainer)} onClick={(e) => this.onButtonClick(e)}>
                <div className={classNames("cwt-overlay")}>
                    <button onClick={(e) => this.onButtonClick(e)}
                        className={managedClasses.overlayButton}
                        {...renderHybrid(this.telemetryObjects && this.telemetryObjects.readFullContentButton, true)}>
                        { t.OverlayGradientSubscribeText }
                    </button>
                </div>
            </div>
        );
    }
}